import { useEffect, useId, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import type { SidebarItem } from "@/types/navigation";
import { collectOpenGroupIds, hasActiveDescendant } from "./sidebarNavigation";
import { useSidebar } from "@/contexts/SidebarContext";

/* ── Shared design tokens ────────────────────────────────────────────────── */

export const NAV_ITEM_ACTIVE_CLASS =
  "bg-brand-cyan/10 text-brand-navy dark:bg-brand-cyan/12 dark:text-primary";
export const NAV_ITEM_INACTIVE_CLASS =
  "text-secondary hover:bg-surface-elevated hover:text-primary";

/** Height shared by every row — matches the SpaceRail icon slots. */
const ITEM_H = "h-9 pointer-coarse:min-h-11";
const ITEM_RADIUS = "rounded-lg";
const ITEM_TEXT = "text-sm font-medium";
const ICON_SIZE = "h-[18px] w-[18px]";
const ITEM_FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus";

/**
 * Width of the icon slot in expanded mode.
 * Must equal the collapsed sidebar width (60px) so that icons sit at the
 * exact same x position whether the sidebar is collapsed or expanded.
 *
 * Collapsed layout:  [   icon 36px centred in 60px   ]
 * Expanded layout:   [ px-3 | icon 36px | label … ]
 *                     12px    36px
 * The link/button therefore uses px-3 in expanded mode and no horizontal
 * padding + justify-center in collapsed mode.
 */
const ICON_BOX = "h-9 w-9";

const TRANSITION_DURATION = "duration-200";
const GRID_TRANSITION = `transition-[grid-template-rows] ${TRANSITION_DURATION} ease-[cubic-bezier(0.77,0,0.175,1)]`;

/** Groups already opened by `defaultOpen` in this session: a later close is respected. */
const AUTO_OPENED_GROUPS = new Set<string>();

/* ── Props ───────────────────────────────────────────────────────────────── */

interface SidebarNavItemsProps {
  items: SidebarItem[];
  activeItemId: string | null;
  expanded: boolean;
  className: string;
  onNavigate?: () => void;
  onItemMouseEnter?: (label: string, el: HTMLElement) => void;
  onItemMouseLeave?: () => void;
}

/* ── Root ─────────────────────────────────────────────────────────────────── */

export function SidebarNavItems({
  items,
  activeItemId,
  expanded,
  className,
  onNavigate,
  onItemMouseEnter,
  onItemMouseLeave,
}: SidebarNavItemsProps) {
  // Stable ref to latest items — avoids stale closure when items array is
  // reconstructed on every render (e.g. dynamic analytics nav).
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  });

  /* Open-groups state comes from context (persists across re-mounts). */
  const { openGroups, setOpenGroups, toggleGroup } = useSidebar();

  /* On mount and whenever activeItemId changes, auto-open the group that
     contains the active item — never close groups opened manually. Groups
     marked `defaultOpen` open once per session, then follow the person. */
  const defaultOpenIds = items
    .filter((item) => item.defaultOpen && !AUTO_OPENED_GROUPS.has(item.id))
    .map((item) => item.id)
    .join("|");
  useEffect(() => {
    if (!defaultOpenIds) return;
    const ids = defaultOpenIds.split("|");
    for (const id of ids) AUTO_OPENED_GROUPS.add(id);
    setOpenGroups((prev) => (ids.every((id) => prev.has(id)) ? prev : new Set([...prev, ...ids])));
  }, [defaultOpenIds, setOpenGroups]);
  useEffect(() => {
    setOpenGroups((prev) => {
      const required = collectOpenGroupIds(itemsRef.current, activeItemId);
      if ([...required].every((id) => prev.has(id))) return prev;
      const next = new Set(prev);
      for (const id of required) next.add(id);
      return next;
    });
  }, [activeItemId, setOpenGroups]);

  return (
    <nav className={className} aria-label="Section navigation">
      <ul className="m-0 flex min-w-0 list-none flex-col gap-y-1 p-0">
        {items.map((item) => (
          <NavNode
            key={item.id}
            item={item}
            depth={0}
            expanded={expanded}
            activeItemId={activeItemId}
            openGroups={openGroups}
            toggleGroup={toggleGroup}
            onNavigate={onNavigate}
            onMouseEnter={onItemMouseEnter}
            onMouseLeave={onItemMouseLeave}
          />
        ))}
      </ul>
    </nav>
  );
}

/* ── Recursive node ──────────────────────────────────────────────────────── */

interface NavNodeProps {
  item: SidebarItem;
  depth: number;
  expanded: boolean;
  activeItemId: string | null;
  openGroups: Set<string>;
  toggleGroup: (id: string) => void;
  onNavigate?: () => void;
  onMouseEnter?: (label: string, el: HTMLElement) => void;
  onMouseLeave?: () => void;
}

function NavNode({
  item,
  depth,
  expanded,
  activeItemId,
  openGroups,
  toggleGroup,
  onNavigate,
  onMouseEnter,
  onMouseLeave,
}: NavNodeProps) {
  const hasChildren = (item.children?.length ?? 0) > 0;
  const isActive = activeItemId === item.id;
  const isOpen = openGroups.has(item.id);
  const childRegionId = useId();

  /* ── Collapsed top-level: icon only, centred in the 60px rail ─────────── */
  if (!expanded && depth === 0) {
    const href = item.href || item.children?.[0]?.href || "";
    // Children are hidden when collapsed, so a group must reflect its active child.
    const collapsedActive = isActive || hasActiveDescendant(item, activeItemId);

    return (
      <li className="relative">
        <Link
          to={href}
          onClick={onNavigate}
          aria-label={item.label}
          aria-current={collapsedActive ? "page" : undefined}
          onMouseEnter={(e) => onMouseEnter?.(item.label, e.currentTarget)}
          onMouseLeave={onMouseLeave}
          onFocus={(event) => onMouseEnter?.(item.label, event.currentTarget)}
          onBlur={onMouseLeave}
          className={cn(
            /* Fill the full 60px width, centre the icon */
            "flex w-full items-center justify-center outline-none cursor-pointer no-underline!",
            ITEM_H,
            ITEM_RADIUS,
            ITEM_TEXT,
            ITEM_FOCUS,
            "transition-colors",
            collapsedActive ? NAV_ITEM_ACTIVE_CLASS : NAV_ITEM_INACTIVE_CLASS,
          )}
        >
          {item.icon && (
            <span className={cn("flex shrink-0 items-center justify-center", ICON_BOX)}>
              <item.icon className={cn(ICON_SIZE, "opacity-70")} />
            </span>
          )}
        </Link>
      </li>
    );
  }

  /* ── Expanded leaf ────────────────────────────────────────────────────── */
  if (!hasChildren) {
    return (
      <li className="relative">
        <Link
          to={item.href}
          onClick={onNavigate}
          aria-current={isActive ? "page" : undefined}
          aria-label={item.label}
          onMouseEnter={(event) => onMouseEnter?.(item.label, event.currentTarget)}
          onMouseLeave={onMouseLeave}
          onFocus={(event) => onMouseEnter?.(item.label, event.currentTarget)}
          onBlur={onMouseLeave}
          className={cn(
            "group/menu-button relative flex w-full min-w-0 items-center outline-none cursor-pointer no-underline!",
            item.meta ? "min-h-11 py-1" : ITEM_H,
            ITEM_RADIUS,
            ITEM_TEXT,
            ITEM_FOCUS,
            depth === 0 ? "px-3 gap-2.5" : "px-3 gap-2",
            "transition-colors",
            isActive ? NAV_ITEM_ACTIVE_CLASS : NAV_ITEM_INACTIVE_CLASS,
          )}
        >
          {depth === 0 && item.icon && (
            <span className={cn("flex shrink-0 items-center justify-center", ICON_BOX)}>
              <item.icon className={cn(ICON_SIZE, "opacity-70")} />
            </span>
          )}

          <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-left">
            <span className="min-w-0 flex-1">
              <span className="block truncate">{item.label}</span>
              {item.meta ? (
                <span className="block truncate text-xs font-normal text-muted">{item.meta}</span>
              ) : null}
            </span>
            {item.badge && <Badge text={item.badge} />}
          </span>
        </Link>
      </li>
    );
  }

  /* ── Expanded group (collapsible) ────────────────────────────────────── */
  return (
    <li className="relative">
      <button
        type="button"
        onClick={() => toggleGroup(item.id)}
        aria-expanded={isOpen}
        aria-controls={childRegionId}
        aria-label={isOpen ? `Collapse ${item.label}` : `Expand ${item.label}`}
        onMouseEnter={(event) => onMouseEnter?.(item.label, event.currentTarget)}
        onMouseLeave={onMouseLeave}
        onFocus={(event) => onMouseEnter?.(item.label, event.currentTarget)}
        onBlur={onMouseLeave}
        className={cn(
          "group/menu-button relative flex w-full min-w-0 items-center outline-none cursor-pointer",
          ITEM_H,
          ITEM_RADIUS,
          ITEM_TEXT,
          ITEM_FOCUS,
          depth === 0 ? "px-3 gap-2.5" : "px-3 gap-2",
          "transition-colors",
          isActive ? NAV_ITEM_ACTIVE_CLASS : NAV_ITEM_INACTIVE_CLASS,
        )}
      >
        {depth === 0 && item.icon && (
          <span className={cn("flex shrink-0 items-center justify-center", ICON_BOX)}>
            <item.icon className={cn(ICON_SIZE, "opacity-70")} />
          </span>
        )}

        <span className="flex flex-1 items-center gap-2 min-w-0 text-left overflow-hidden">
          <span className="truncate">{item.label}</span>
          {item.badge && <Badge text={item.badge} />}

          <ChevronRight
            className={cn(
              "ml-auto h-3 w-3 shrink-0 opacity-40",
              "group-hover/menu-button:opacity-100",
              "origin-center transition-transform duration-350 ease-in-out motion-reduce:transition-none",
              isOpen && "rotate-90",
            )}
          />
        </span>
      </button>

      {/* Collapsible region – grid-rows animation */}
      <div
        id={childRegionId}
        role="region"
        aria-label={`${item.label} items`}
        className={cn(
          "grid motion-reduce:transition-none",
          GRID_TRANSITION,
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
        inert={!isOpen || undefined}
      >
        <div className="overflow-hidden">
          <ul
            className={cn(
              "relative m-0 flex min-w-0 list-none flex-col gap-y-1 p-0",
              depth === 0 ? "pl-12 pr-0" : "pl-5 pr-0",
            )}
          >
            {/* Vertical connector line */}
            <li
              aria-hidden="true"
              className={cn(
                "absolute inset-y-px w-px bg-border z-10",
                depth === 0 ? "left-7.5" : "left-2.5",
              )}
            />

            {item.children!.map((child) => (
              <NavNode
                key={child.id}
                item={child}
                depth={depth + 1}
                expanded={expanded}
                activeItemId={activeItemId}
                openGroups={openGroups}
                toggleGroup={toggleGroup}
                onNavigate={onNavigate}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              />
            ))}
          </ul>
        </div>
      </div>
    </li>
  );
}

/* ── Small helpers ────────────────────────────────────────────────────────── */

function Badge({ text }: { text: string }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full border border-dashed border-border select-none px-1.5 py-0.5 text-xs/none font-medium text-primary">
      {text}
    </span>
  );
}
